import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from 'react';
import {
  View,
  StyleSheet,
  RefreshControl,
  FlatList,
  StatusBar,
  ActivityIndicator,
} from 'react-native';
import { Text, useTheme, Chip, SegmentedButtons } from 'react-native-paper';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import { Coffee } from 'lucide-react-native';
import { CreatorCard, CreatorSearch } from '../components/creators';
import { ScrollToTopFAB } from '../components/ui';
import { creatorApi, Creator } from '../services/api/creatorApi';
import { logger } from '../utils/logger';

export function CreatorsScreen() {
  const theme = useTheme();
  const navigation = useNavigation();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSpecialty, setSelectedSpecialty] = useState('all');
  const [activeView, setActiveView] = useState('all');
  const [refreshing, setRefreshing] = useState(false);
  const [creators, setCreators] = useState<Creator[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const flatListRef = useRef<FlatList>(null);

  // Fetch creators from API
  const fetchCreators = useCallback(async () => {
    try {
      setError(null);
      const response = await creatorApi.getCreators({
        page: 1,
        size: 50,
        search: searchQuery || undefined,
      });
      setCreators(response.creators);
    } catch (err: any) {
      logger.error('Error fetching creators:', err.message);
      setError('Failed to load creators. Please try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [searchQuery]);

  // Fetch creators on mount and when search query changes
  useEffect(() => {
    fetchCreators();
  }, [fetchCreators]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchCreators();
  }, [fetchCreators]);

  const handleCreatorPress = useCallback(
    (creator: any) => {
      console.log('Creator pressed:', creator);
      (navigation as any).navigate('creatorProfile', {
        creatorId: creator.id,
      });
    },
    [navigation]
  );

  const handleScrollToTop = useCallback(() => {
    flatListRef.current?.scrollToOffset({ animated: true, offset: 0 });
  }, []);

  const handleScroll = useCallback((event: any) => {
    const yOffset = event.nativeEvent.contentOffset.y;
    setShowScrollToTop(yOffset > 300);
  }, []);

  const filteredCreators = useMemo(() => {
    return creators.filter(creator => {
      const matchesSearch =
        creator.firstName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        creator.lastName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        creator.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
        creator.bio.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesSearch;
    });
  }, [creators, searchQuery]);

  const renderCreatorCard = useCallback(
    ({ item: creator }: { item: any }) => (
      <View style={styles.cardContainer}>
        <CreatorCard
          creator={creator}
          onPress={handleCreatorPress}
          testID={`creator-${creator.id}`}
        />
      </View>
    ),
    [handleCreatorPress]
  );

  return (
    <SafeAreaView
      style={[styles.container, { backgroundColor: theme.colors.background }]}
    >
      <StatusBar
        barStyle={theme.dark ? 'light-content' : 'dark-content'}
        backgroundColor='transparent'
        translucent
      />

      {/* Search */}
      <View style={styles.searchContainer}>
        <CreatorSearch
          searchQuery={searchQuery}
          selectedSpecialty={selectedSpecialty}
          onSearchChange={setSearchQuery}
          onSpecialtyFilter={() => {}}
        />
      </View>

      {/* Loading State */}
      {loading && !refreshing && (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size='large' color={theme.colors.primary} />
          <Text
            variant='bodyMedium'
            style={[
              styles.loadingText,
              { color: theme.colors.onSurfaceVariant },
            ]}
          >
            Loading creators...
          </Text>
        </View>
      )}

      {/* Error State */}
      {error && !loading && (
        <View style={styles.emptyState}>
          <View style={styles.emptyGradient}>
            <Coffee size={56} color={theme.colors.error} />
            <Text
              variant='titleLarge'
              style={[styles.emptyTitle, { color: theme.colors.onSurface }]}
            >
              Something went wrong
            </Text>
            <Text
              variant='bodyMedium'
              style={[
                styles.emptySubtitle,
                { color: theme.colors.onSurfaceVariant },
              ]}
            >
              {error}
            </Text>
          </View>
        </View>
      )}

      {/* Creators List */}
      {!loading && !error && (
        <FlatList
          ref={flatListRef}
          data={filteredCreators}
          renderItem={renderCreatorCard}
          keyExtractor={item => item?.id}
          contentContainerStyle={[styles.creatorsList, { paddingBottom: 100 }]}
          showsVerticalScrollIndicator={false}
          onScroll={handleScroll}
          scrollEventThrottle={16}
          removeClippedSubviews={true}
          maxToRenderPerBatch={10}
          updateCellsBatchingPeriod={50}
          initialNumToRender={10}
          windowSize={10}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={onRefresh}
              colors={[theme.colors.primary]}
              tintColor={theme.colors.primary}
            />
          }
          ListEmptyComponent={
            <View style={styles.emptyState}>
              <View style={styles.emptyGradient}>
                <Coffee size={56} color={theme.colors.onSurfaceVariant} />
                <Text
                  variant='titleLarge'
                  style={[styles.emptyTitle, { color: theme.colors.onSurface }]}
                >
                  No creators found
                </Text>
                <Text
                  variant='bodyMedium'
                  style={[
                    styles.emptySubtitle,
                    { color: theme.colors.onSurfaceVariant },
                  ]}
                >
                  Adjust your search
                </Text>
              </View>
            </View>
          }
        />
      )}

      {/* Scroll to Top FAB */}
      <ScrollToTopFAB
        visible={showScrollToTop}
        onPress={handleScrollToTop}
        testID='creators-scroll-to-top'
      />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  searchContainer: {
    paddingHorizontal: 16,
  },
  creatorsList: {
    paddingTop: 0,
  },
  cardContainer: {
    paddingHorizontal: 8,
    marginBottom: 8,
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 80,
    paddingHorizontal: 32,
  },
  emptyGradient: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
    borderRadius: 20,
  },
  emptyTitle: {
    marginTop: 24,
    marginBottom: 12,
    textAlign: 'center',
    fontWeight: '600',
  },
  emptySubtitle: {
    textAlign: 'center',
    lineHeight: 22,
    opacity: 0.8,
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 80,
  },
  loadingText: {
    marginTop: 16,
    textAlign: 'center',
  },
});
